import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { SortDirection, buildPagedSortQueryParams } from '../utils/pagedSortQueryParams';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'legalMatter/';

export type LegalMatterSortField = 'openDate' | 'matterName' | 'stage' | 'status';

export function useLegalMatterFilter({
	filterRequest,
	pageSize = 25,
	sortBy = 'openDate',
	sort = 'desc',
	enabled = true,
	refetchOnWindowFocus = false,
	expand = 'contact',
}: {
	filterRequest: Api.ILegalMatterFilterRequest;
	pageSize?: number;
	sortBy?: LegalMatterSortField;
	sort?: SortDirection;
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
	expand?: string;
}) {
	const userSession = useUserSession();

	return useInfiniteQuery<Api.IPagedCollection<Api.ILegalMatter>>({
		enabled,
		getNextPageParam: lastPage => lastPage.pageToken,
		queryFn: async ({ pageParam }) => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.ILegalMatter>>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: buildPagedSortQueryParams({ pageSize, pageToken: pageParam, sortBy, sort, expand }),
					urlPath: 'LegalMatter/filter',
				}),
				'POST',
				filterRequest
			);
		},
		refetchOnWindowFocus,
		queryKey: [BASE_QUERY_KEY, filterRequest, pageSize, sortBy, sort, expand],
		initialPageParam: '',
	});
}

export const invalidateLegalMatterFilter = ({
	filterRequest,
	sortBy = 'openDate',
	sort = 'desc',
	expand = 'contact',
	pageSize = 25,
}: {
	filterRequest: Api.ILegalMatterFilterRequest;
	sortBy?: LegalMatterSortField;
	sort?: SortDirection;
	expand?: string;
	pageSize?: number;
}) => {
	reactQueryClient.invalidateQueries({
		queryKey: [BASE_QUERY_KEY, filterRequest, pageSize, sortBy, sort, expand],
	});
};

export const invalidateAllLegalMatterFilters = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};
